import Icons from "@/assets/icons";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { useAuth } from "@/contexts/Auth";
import { ScrollArea } from "@radix-ui/react-scroll-area";
import { useEffect, useState } from "react";
import Input from "./Input";
import { ApiService } from "@/services/ApiService";
import { useFormik } from "formik";
import Button from "./Button";
import v from "@/lib/validator";
import { validateFormikFields } from "@/lib/utils";
import { t } from "i18next";
import { toast } from "sonner";
import { messageService } from "@/services/MessageService";

interface DialogProps {
  open: boolean;
  setOpen: any;
}

const UserProfileDialog: React.FC<DialogProps> = ({ open, setOpen }) => {
  const { usuario } = useAuth();
  const [isChangePwd, setIsChangePwd] = useState(false);
  const { patch } = ApiService;

  const changePwdSchema = (formikValues?: any) => {
    const passwordValidations = v
      .methods()
      .min({
        length: 6,
        message: t("sidebar.userProfile.inputs.errors.minChar"),
      })
      .max({
        length: 24,
        message: t("sidebar.userProfile.inputs.errors.maxChar"),
      })
      .regex({
        pattern: /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d).+$/,
        message: t("sidebar.userProfile.inputs.errors.regex"),
      });
    const matchPasswdValidation = v.methods().isEquals({
      otherValue: formikValues?.novaSenhaConfirma,
      message: t("sidebar.userProfile.inputs.errors.match"),
    });
    const matchPasswdConfirmValidation = v.methods().isEquals({
      otherValue: formikValues?.novaSenha,
      message: t("sidebar.userProfile.inputs.errors.match"),
    });

    const schema = v.object({
      senha: passwordValidations,
      novaSenha: v.methods().use([passwordValidations, matchPasswdValidation]),
      novaSenhaConfirma: v
        .methods()
        .use([passwordValidations, matchPasswdConfirmValidation]),
    });

    return schema;
  };

  const [schema, setSchema] = useState(() =>
    changePwdSchema({
      novaSenha: "",
      novaSenhaConfirma: "",
    })
  );

  const formikData = useFormik({
    initialValues: {
      senha: "",
      novaSenha: "",
      novaSenhaConfirma: "",
    },
    validate: (values) => validateFormikFields(values, schema),
    onSubmit: async (values) => {
      try {
        const { status } = await patch(
          `api/v1/usuario/mudarSenha/${usuario.id}`,
          values
        );

        if (status === 200)
          toast.success(t("sidebar.userProfile.messages.toast"));
      } catch (e: any) {
        messageService.errorWithDetail(
          t("sidebar.userProfile.messages.error"),
          e.data?.detail ?? e,
          () => setOpen(true)
        );
        setOpen(false);
      }
    },
  });

  useEffect(() => {
    if (formikData.values) {
      setSchema(changePwdSchema(formikData.values));
    }
  }, [formikData.values.novaSenha, formikData.values.novaSenhaConfirma]);

  // Constantes de estilo
  const profileSectionStyle =
    "grid sm:grid-cols-[auto_1fr] items-center gap-4 mb-4 max-sm:text-center";
  const profileAvatarStyle =
    "justify-self-start max-sm:justify-self-center bg-neutro-100 p-6 rounded-full";
  const permSectionListStyle =
    "uppercase flex gap-2 py-2 border-b border-neutro-100";
  const scrollAreaStyle =
    "max-h-100 overflow-y-auto [&::-webkit-scrollbar]:w-1 [&::-webkit-scrollbar-track]:bg-gray-100 [&::-webkit-scrollbar-thumb]:bg-gray-300";
  return (
    <Dialog open={open} onOpenChange={setOpen} defaultOpen>
      <DialogContent className="!max-w-xl" aria-describedby="Perfil do usuário">
        <DialogHeader className="mb-4">
          <DialogTitle className="text-2xl">
            {t(
              isChangePwd
                ? "sidebar.userProfile.titles.form"
                : "sidebar.userProfile.titles.profile"
            )}
          </DialogTitle>
        </DialogHeader>
        {!isChangePwd ? (
          <div className="mb-4">
            <section className={profileSectionStyle}>
              <div className={profileAvatarStyle}>
                <Icons.Person
                  width={32}
                  height={32}
                  pathStyle="fill-contraste-100"
                />
              </div>

              <div className="grid grid-cols-2 sm:gap-4">
                <span className="border-r border-neutro-100">
                  <h3 className="font-medium text-xl">{usuario.nome}</h3>
                  <p className="text-neutro-600">
                    {usuario.perfil && usuario.perfil.length > 0
                      ? usuario.perfil.charAt(0) +
                        usuario.perfil.slice(1).toLowerCase()
                      : ""}
                  </p>
                </span>

                <span>
                  <h3 className="font-medium text-xl">E-mail</h3>
                  <p className="text-neutro-600">{usuario.email}</p>
                </span>
              </div>
            </section>

            <ScrollArea className={scrollAreaStyle}>
              <section className="max-sm:text-center mb-4">
                <h1 className="font-medium text-2xl mb-4">
                  {t("sidebar.userProfile.titles.perms")}
                </h1>
                <ul className="grid sm:grid-cols-2 ">
                  {usuario.permissoes?.map((perm) => (
                    <li key={perm} className={permSectionListStyle}>
                      {<Icons.Check />} {t("sidebar.userProfile.permPrefix")}{" "}
                      <strong>{perm.split("_")[1]}</strong>
                    </li>
                  ))}
                </ul>
              </section>
            </ScrollArea>
          </div>
        ) : (
          <form onSubmit={formikData.handleSubmit}>
            <Input
              id="password"
              label={t("sidebar.userProfile.inputs.password")}
              containerClassProps="mb-4"
              type="password"
              error={
                formikData.errors.senha && formikData.touched.senha
                  ? formikData.errors.senha
                  : ""
              }
              {...formikData.getFieldProps("senha")}
            />
            <Input
              id="new-password"
              label={t("sidebar.userProfile.inputs.newPassword")}
              containerClassProps="mb-4"
              type="password"
              error={
                formikData.errors.novaSenha && formikData.touched.novaSenha
                  ? formikData.errors.novaSenha
                  : ""
              }
              {...formikData.getFieldProps("novaSenha")}
            />
            <Input
              id="confirm-new-password"
              label={t("sidebar.userProfile.inputs.newPasswordConfirm")}
              containerClassProps="mb-4"
              type="password"
              error={
                formikData.errors.novaSenhaConfirma &&
                formikData.touched.novaSenhaConfirma
                  ? formikData.errors.novaSenhaConfirma
                  : ""
              }
              {...formikData.getFieldProps("novaSenhaConfirma")}
            />

            <div className="flex flex-wrap justify-between">
              <Button
                color="primario"
                label={t("sidebar.userProfile.buttons.send")}
                type="submit"
                disabled={formikData.isSubmitting}
                classProps="p-3"
              />
              <Button
                color="neutro"
                label={t("sidebar.userProfile.buttons.cancel")}
                onClick={() => {
                  setIsChangePwd(false);
                  formikData.resetForm();
                }}
                type="button"
                classProps="p-3"
              />
            </div>
          </form>
        )}
        {!isChangePwd && (
          <DialogFooter>
            <Button
              color="primario"
              label={t("sidebar.userProfile.buttons.changePassword")}
              onClick={() => setIsChangePwd(true)}
              type="button"
              classProps="p-3"
            />
          </DialogFooter>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default UserProfileDialog;
